import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/public-layout";

const PROSE =
  "prose prose-gray max-w-none " +
  "prose-headings:font-semibold prose-headings:text-gray-900 " +
  "prose-h1:text-3xl sm:prose-h1:text-4xl prose-h1:tracking-tight " +
  "prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3 " +
  "prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-2 " +
  "prose-p:text-gray-700 prose-li:text-gray-700 " +
  "prose-a:text-primary prose-a:no-underline hover:prose-a:underline";

export function TermsPage() {
  const canonicalUrl = `${window.location.origin}/terms`;

  return (
    <PublicLayout>
      <Helmet>
        <title>Terms of Use · DealerDog</title>
        <meta
          name="description"
          content="DealerDog Terms of Use and Dealer Platform Terms for TrueCore Capital, LLC d/b/a DealerDog."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Terms of Use · DealerDog" />
        <meta
          property="og:description"
          content="Terms governing access to the DealerDog platform and additional terms for dealers."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className={PROSE}>
          <h1>DealerDog Terms of Use &amp; Dealer Platform Terms</h1>
          <p>
            <strong>Effective Date:</strong> November 3, 2026
            <br />
            <strong>Last Updated:</strong> November 3, 2026
          </p>

          <p>
            These Terms of Use (the “Terms”) form an agreement between you and TrueCore Capital, LLC,
            doing business as DealerDog (“TrueCore,” “DealerDog,” “Company,” “we,” “us,” or “our”),
            governing access to dealer.dog and any Company-operated DealerDog websites, dealer portals,
            software, applications, integrations, and related online services (collectively, the
            “Platform”). Part A applies to all Platform users. Part B applies additionally to dealers,
            merchants, referral sources, and other business account holders (“Dealers”).
          </p>
          <p>
            <strong>IMPORTANT:</strong> These Terms govern Platform access and use. They do not replace
            a signed dealer program agreement, a customer financing agreement, a credit authorization,
            or legally required notices or disclosures. Financing applications and transactions may also
            be conducted outside the Platform.
          </p>

          <h2>PART A — GENERAL TERMS</h2>

          <h3>1. Acceptance; Authority</h3>
          <p>
            By accessing or using the Platform, creating an account, or clicking to accept these Terms,
            you agree to be bound by them. If acting for an entity, you represent that you have
            authority to bind it. If you do not agree, do not use the Platform. Additional written
            agreements, consents, financing documents, and legally required disclosures apply according
            to their terms. Where a separately executed agreement with Company expressly conflicts with
            these Terms, that executed agreement controls for its subject matter.
          </p>

          <h3>2. Operator and Business Purpose</h3>
          <p>
            DealerDog is a trade name (DBA) of TrueCore Capital, LLC, not a separate legal entity. The
            Platform supports commercial and business-purpose equipment financing activities. It is not
            intended to solicit personal, family, or household financing. Users must be at least 18 and
            legally capable of entering contracts. Company may request proof of identity, business
            status, authority, or eligibility.
          </p>

          <h3>3. Our Role in Commercial Financing</h3>
          <p>
            TrueCore may act as a commercial financing broker, referral or placement intermediary,
            program administrator, or, where applicable and expressly documented, a direct financing
            provider or other transaction party. Company may review, process, verify, refer, structure,
            or administer financing requests and communicate with third-party financing sources,
            underwriters, lessors, purchasers, assignees, service providers, and other transaction
            participants (“Financing Sources”). The precise capacity of each party is determined by the
            applicable transaction documents. Use of the Platform does not guarantee that Company or any
            Financing Source will extend credit, purchase an agreement, approve an application, or fund
            a transaction.
          </p>
          <p>
            TrueCore may be identified in financing documents as the initial lessor, creditor or secured
            party and may subsequently assign the agreement and related rights to a Financing Source.
          </p>

          <h3>4. Applications and Authorized Submission Channels</h3>
          <p>
            Financing inquiries, applications, documents, and communications may be submitted through
            the Platform or through other Company-approved channels, including signed paper forms,
            email, secure file transfer, direct contact with Company personnel, and approved third-party
            application systems. Company may accept, reject, supplement, verify, or request resubmission
            of information regardless of intake channel. Nothing in these Terms makes the Platform the
            exclusive application or authorization channel. A submission does not obligate Company or a
            Financing Source to process, approve, or fund a transaction.
          </p>

          <h3>5. Credit Information; Permissible Purpose; Authorizations</h3>
          <p>
            Company and applicable Financing Sources may obtain consumer reports regarding owners,
            principals, or guarantors and commercial reports regarding businesses only as permitted by
            applicable law and for an authorized permissible purpose. Where consent or authorization is
            required, it must be obtained in a legally sufficient form, which may include a signed paper
            authorization, a valid electronic authorization, or another lawful method. These Terms
            themselves are not a consumer-report authorization and do not substitute for any separate
            required disclosure or consent. A Dealer may collect and transmit an authorization actually
            given by the relevant individual, but may not impersonate the individual, forge a signature,
            or manufacture consent. Company may verify authorization and decline to process a submission
            if adequate evidence is unavailable. Revocation or withdrawal will be handled as required by
            law and may affect processing.
          </p>

          <h3>6. Estimates; No Commitment</h3>
          <p>
            Prequalification results, pricing illustrations, payment estimates, and preliminary
            indications are informational and may change. They are not binding approvals, offers,
            commitments, or guarantees. All financing is subject to underwriting, verification,
            documentation, collateral, funding conditions, and execution of definitive agreements.
            Taxes, fees, insurance, and other charges may not be reflected in estimates. Company may
            correct errors and withdraw or modify preliminary terms, subject to applicable law and
            binding written commitments.
          </p>

          <h3>7. Accuracy; Verification; Cooperation</h3>
          <p>
            You represent that information and documents you submit are accurate, current, authentic,
            and submitted with appropriate authority. You will promptly correct material changes and
            cooperate with reasonable verification requests. Company may use identity, fraud, sanctions,
            business, banking, title, collateral, and other verification methods as permitted by law.
            Company may reject incomplete, inconsistent, suspicious, or unauthorized submissions and
            preserve relevant records as legally required.
          </p>

          <h3>8. Accounts and Security</h3>
          <p>
            You are responsible for authorized activity under your credentials, protecting passwords and
            authentication methods, limiting access to appropriate personnel, and notifying Company
            promptly of suspected compromise at{" "}
            <a href="mailto:support@truecorecapital.com">support@truecorecapital.com</a>. Accounts and
            credentials may not be shared or transferred without authorization. Company may impose
            security controls, access limits, identity verification, and account reviews.
          </p>

          <h3>9. Prohibited Conduct</h3>
          <p>You may not:</p>
          <ul>
            <li>submit fraudulent, forged, misleading, or unauthorized information;</li>
            <li>bypass security, consent, identity-verification, or access controls;</li>
            <li>
              scrape, harvest, reverse engineer, or automate Platform access without written
              authorization;
            </li>
            <li>introduce malware or disrupt operations;</li>
            <li>access another party’s information without authorization;</li>
            <li>remove or alter legal notices or disclosures;</li>
            <li>misrepresent financing terms or approval status;</li>
            <li>
              use the Platform in violation of applicable credit-reporting, privacy, fair-lending,
              consumer-protection, or other laws; or
            </li>
            <li>resell or sublicense the Platform without written permission.</li>
          </ul>

          <h3>10. Platform License and Intellectual Property</h3>
          <p>
            Subject to compliance with these Terms, Company grants a limited, revocable, nonexclusive,
            nontransferable license to use the Platform for its intended business purposes. Company and
            its licensors retain all rights to software, trademarks, content, designs, and technology.
            No ownership rights are transferred. Feedback may be used to improve services without
            compensation, subject to applicable confidentiality obligations.
          </p>

          <h3>11. Third Parties and Integrations</h3>
          <p>
            The Platform may connect to Financing Sources and third-party identity, credit, e-signature,
            analytics, hosting, communications, and other providers. Third parties may have their own
            terms and privacy practices. Company is not responsible for third-party acts or outages to
            the extent permitted by law, but nothing here disclaims duties that Company cannot lawfully
            waive.
          </p>

          <h3>12. Privacy; Data Handling</h3>
          <p>
            Our Privacy Policy describes relevant collection, use, sharing, retention, and protection
            practices. Specific application notices, credit authorizations, privacy notices, and
            transaction documents may impose additional requirements. You must not upload information
            you lack authority to disclose. Company may use and disclose information to evaluate,
            process, service, secure, audit, and administer transactions and to comply with law, subject
            to applicable restrictions.
          </p>

          <h3>13. Electronic Communications and Signatures</h3>
          <p>
            Where permitted by law and after any required separate consent, Company may communicate
            electronically and use electronic records and signatures. This provision does not by itself
            supply legally required E-SIGN consumer consent, telemarketing consent, or consent to
            automated calls or texts. Transaction-specific consents and opt-out rights remain
            controlling. Paper documents and handwritten signatures may also be accepted.
          </p>

          <h3>14. Availability; Changes to Platform</h3>
          <p>
            Company may modify, maintain, suspend, or discontinue Platform functions, subject to
            applicable contractual and legal obligations. Company does not guarantee uninterrupted
            availability, error-free operation, or permanent retention of user-accessible copies. Users
            should retain their own required business records and should not rely on the Platform as
            their sole archive.
          </p>

          <h3>15. Disclaimers</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM IS PROVIDED “AS IS” AND “AS
            AVAILABLE,” WITHOUT WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, OR UNINTERRUPTED OPERATION. COMPANY DOES NOT WARRANT APPROVAL, FUNDING, A
            PARTICULAR RATE OR TERM, OR THE ACCURACY OF THIRD-PARTY DATA. NOTHING IN THESE TERMS
            EXCLUDES A WARRANTY OR OBLIGATION THAT CANNOT LAWFULLY BE EXCLUDED.
          </p>

          <h3>16. Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANY AND ITS OFFICERS, EMPLOYEES, AND AGENTS WILL
            NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE
            DAMAGES OR LOST PROFITS ARISING FROM PLATFORM USE. SUBJECT TO NON-WAIVABLE LAW AND ANY
            SEPARATELY EXECUTED AGREEMENT, COMPANY’S AGGREGATE LIABILITY FOR PLATFORM-RELATED CLAIMS
            WILL NOT EXCEED THE GREATER OF $1,000 OR FEES ACTUALLY PAID BY CLAIMANT TO COMPANY
            SPECIFICALLY FOR PLATFORM ACCESS DURING THE PRECEDING 12 MONTHS. THIS LIMIT DOES NOT PURPORT
            TO LIMIT LIABILITY FOR FRAUD, WILLFUL MISCONDUCT, OR OTHER LIABILITY THAT APPLICABLE LAW
            PROHIBITS LIMITING.
          </p>

          <h3>17. Indemnification</h3>
          <p>
            To the extent permitted by law, business users will defend, indemnify and hold harmless
            Company and its officers, employees and agents from third-party claims, damages,
            liabilities and reasonable expenses, including attorneys’ fees, arising from their fraud,
            unauthorized submissions, misuse of customer information, material breach of these Terms or
            violation of law, except to the extent caused by Company’s own negligence, willful
            misconduct or other wrongful conduct.
          </p>
          <p>
            Company will promptly notify the business user of a covered claim. Delayed notice relieves
            the business user of its obligations only to the extent the delay materially prejudices the
            defense. The business user will control the defense using reasonably qualified counsel, and
            Company will reasonably cooperate at the business user’s expense. Company may participate
            through its own counsel at its own expense.
          </p>
          <p>
            The business user may not settle a claim without Company’s prior written consent if the
            settlement admits fault by Company, imposes nonmonetary obligations on Company or fails to
            fully release Company from the covered claim. Company will not unreasonably withhold its
            consent.
          </p>
          <p>
            Any conflicting indemnification, defense or settlement provision in a controlling signed
            agreement governs its specific subject matter.
          </p>

          <h3>18. Suspension; Termination; Record Retention</h3>
          <p>
            Company may restrict or suspend access when reasonably necessary for suspected fraud,
            security risks, legal compliance, misuse, or material breach. Users may cease Platform use,
            but termination does not cancel pending obligations, executed financing documents, legally
            required record retention, or rights accrued before termination. Company may retain records
            as permitted or required by law and its applicable retention policies.
          </p>

          <h3>19. Governing Law; Disputes</h3>
          <p>
            These Terms are governed by California law and applicable federal law, without regard to
            conflicts rules. Unless a controlling signed agreement provides otherwise, the parties
            consent to exclusive jurisdiction in state and federal courts located in Los Angeles County,
            California, to the extent legally enforceable. Nothing prevents a party from seeking
            appropriate emergency relief or exercising non-waivable statutory rights.
          </p>

          <h3>20. Updates; Miscellaneous</h3>
          <p>
            Company may revise these Terms prospectively by posting an updated version and giving any
            notice required by law or applicable agreement. Material changes affecting Dealers may
            require renewed affirmative acceptance. Continued use after effective notice may constitute
            acceptance where enforceable. If any provision is unenforceable, remaining provisions
            survive. Failure to enforce is not waiver. You may not assign your rights without Company’s
            consent. Headings are for convenience. These Terms, together with incorporated policies and
            applicable additional agreements, constitute the agreement concerning Platform use, subject
            to the express precedence stated above.
          </p>

          <h3>21. Contact</h3>
          <p>
            TrueCore Capital, LLC d/b/a DealerDog
            <br />
            28245 Avenue Crocker, Suite 200
            <br />
            Valencia, CA 91355
          </p>
          <p>
            Email:{" "}
            <a href="mailto:support@truecorecapital.com">support@truecorecapital.com</a>
            <br />
            Phone:{" "}
            <a href="tel:+18053876823">805-387-6823</a>
          </p>

          <h2>PART B — ADDITIONAL DEALER PLATFORM TERMS</h2>

          <h3>D1. Dealer Enrollment; Authority; Users</h3>
          <p>
            Dealer represents that it is duly organized or otherwise lawfully operating, has authority
            to participate, and will provide accurate business and ownership information. Dealer is
            responsible for its personnel, users, permissions, credentials, and compliance with these
            Terms. Dealer must promptly revoke access for separated or unauthorized users.
          </p>

          <h3>D2. Customer Permission; Authorization; Submission</h3>
          <p>
            Dealer may refer customers and submit applications and documents through any
            Company-approved channel, including the Platform, paper applications, email, or secure
            transfer. Before transmitting customer information, Dealer must obtain all legally required
            customer permissions, notices, and authorizations and must use Company-approved forms or
            language when instructed. Dealer may assist customers and transmit genuine customer-executed
            paper or electronic authorizations; Dealer may not sign for, impersonate, or fabricate
            consent from a customer or guarantor. Dealer must retain legally sufficient evidence of
            required authorizations for the period specified in its signed dealer agreement or
            applicable law, and promptly produce it upon request. Company may independently confirm
            consent or require direct customer execution.
          </p>

          <h3>D3. Dealer Data Responsibilities</h3>
          <p>
            Dealer must use customer information only for authorized financing and related transaction
            purposes; apply appropriate administrative, technical, and physical safeguards; limit access
            to personnel with a legitimate need; and not sell or use information for unrelated marketing
            without a separate lawful basis and required consent. Dealer must comply with applicable
            privacy, security, and recordkeeping requirements, including any obligations imposed by its
            separate agreements. Dealer remains responsible for information it collects or maintains
            outside the Platform.
          </p>

          <h3>D4. Accuracy; No Misrepresentation</h3>
          <p>
            Dealer must accurately describe equipment, pricing, down payments, ownership, customer
            identity, and material transaction facts; disclose known fraud indicators, side agreements,
            rebates, or material changes; and refrain from promising approval, rates, funding timing, or
            terms without express authorization. Dealer may not characterize TrueCore or any Financing
            Source in a capacity inconsistent with the applicable transaction documents.
          </p>

          <h3>D5. Embeds, API and Branding</h3>
          <p>
            Company may authorize Dealer to use specified DealerDog marks, links, widgets, embedded
            forms, or API credentials on approved properties. Dealer may not alter consent language,
            disclosures, required branding, or security functions; misrepresent an integration; or
            disclose API credentials. Company may withdraw authorization for security, legal, brand, or
            material compliance concerns. Any technical documentation and separate integration agreement
            apply.
          </p>

          <h3>D6. Dealer Website and Marketing Compliance</h3>
          <p>
            Dealer is responsible for its own website content, privacy notices, accessibility,
            advertising, and any legally required customer notices and consents. Dealer must use
            approved financing representations and may not make misleading claims regarding approval,
            rates, credit inquiries, or Company’s role. The parties will coordinate on
            integration-related disclosures and security responsibilities as specified in written
            implementation instructions.
          </p>

          <h3>D7. Fees; Commercial Arrangements</h3>
          <p>
            Any platform fees, referral fees, compensation, chargebacks, reserves, or other commercial
            arrangements must be established in a separate written agreement or order form. These Terms
            alone do not impose a fee or authorize Dealer to collect a fee on Company’s behalf. Dealer
            must not charge or collect a customer financing-related fee on behalf of Company or a
            Financing Source without express written authorization and compliance with applicable law.
          </p>

          <h3>D8. Security Events; Cooperation</h3>
          <p>
            Dealer must notify Company at{" "}
            <a href="mailto:support@truecorecapital.com">support@truecorecapital.com</a> without undue
            delay after discovering suspected unauthorized access, loss or disclosure of customer
            information related to Company transactions and cooperate in investigation, containment,
            notices and remediation. Any specific notification deadline and allocation of
            responsibilities in a signed agreement or applicable law controls. Dealer must preserve
            relevant evidence and refrain from making unauthorized statements on Company’s behalf.
          </p>

          <h3>D9. Separate Dealer Agreement Controls</h3>
          <p>
            If Dealer and TrueCore have entered into a signed dealer program, referral, broker,
            financing, or other written agreement, that agreement governs its specific subject matter
            and controls over inconsistent provisions of Part B. These Terms supplement but do not amend
            or replace such agreement. No Platform click-through modifies a separately signed agreement
            unless the amendment requirements of that agreement are satisfied.
          </p>

          <h3>D10. Termination of Dealer Access</h3>
          <p>
            Company may suspend or terminate Dealer’s Platform access for material breach, suspected
            fraud, security risk, or legal requirements. Termination of access does not terminate a
            separate dealer agreement unless that agreement provides otherwise. On termination, Dealer
            must cease unauthorized use of marks, embeds, and credentials and handle customer records
            under applicable law and controlling agreements.
          </p>
        </div>
      </article>
    </PublicLayout>
  );
}
